import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Opt-in GET response cache. A request is only cached if it explicitly sets
 * this context token to a TTL in milliseconds - everything else passes
 * through untouched. This is for endpoints/actions that don't have (and
 * don't need) an NgRx slice of their own - e.g. near-static reference data
 * (states/LGAs) or one-off lookups - so caching isn't limited to whatever
 * already goes through actions/selectors.
 *
 * Usage in a service:
 *   this.http.get(url, { ...options, context: new HttpContext().set(CACHE_TTL_MS, 60_000) })
 */
export const CACHE_TTL_MS = new HttpContextToken<number>(() => 0);

const MAX_CACHE_ENTRIES = 200;

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly cache = new Map<string, { response: HttpResponse<any>; expiresAt: number }>();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const ttlMs = request.context.get(CACHE_TTL_MS);

    if (request.method !== 'GET' || !ttlMs) {
      return next.handle(request);
    }

    const key = request.urlWithParams;
    const cached = this.cache.get(key);

    if (cached && cached.expiresAt > Date.now()) {
      // Clone the body so a consumer mutating the response in place (some
      // components do) can't corrupt what later cache hits hand back.
      return of(cached.response.clone({ body: this.deepCloneBody(cached.response.body) }));
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.ok) {
          if (this.cache.size >= MAX_CACHE_ENTRIES) {
            const oldestKey = this.cache.keys().next().value;
            if (oldestKey !== undefined) this.cache.delete(oldestKey);
          }
          this.cache.set(key, { response: event, expiresAt: Date.now() + ttlMs });
        }
      })
    );
  }

  private deepCloneBody(body: unknown): unknown {
    return body === null || body === undefined ? body : JSON.parse(JSON.stringify(body));
  }
}
